import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import { ToastContainer, Slide, toast } from 'react-toastify';

import { route } from '~/app/helpers/route';
import { getTotalUnreadBadgeCount, updateBadgeCount } from '~/app/helpers/badge';
import useDidMountEffect from '~/app/hooks/useDidMountEffect';
import { AppHeader } from '~/app/modules/appHeader';
import { AppLoader } from '~/app/modules/appLoader';

import Router from '~/app/Router';

import store, { useStoreState } from '~/store';

import { MACHINES_DETAILS_ROUTES } from '~/machines/Details/Router';

import Sidebar from './Sidebar';
import Content from './Content';
import MainContent from './MainContent';
import Footer from './Footer';
import Messages from './Messages';
import AccessControl from '~/accessControl';
import { PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import PullToRefresh from 'pulltorefreshjs';
import { matchPath } from 'react-router-dom';
import classes from './style.module.scss';
import { REQUESTS_ROUTES } from '~/notifications/Router';
import { TIME_MANAGEMENT_ROUTES } from '~/timeManagement/Router';
import { TRAINING_ROUTES } from '~/training/Router';
import { PROJECTS_ROUTES } from '~/projects/Router';

const isModalOpen = () => !!document.querySelector("[data-app-modal]");

const DISABLED_ROUTES = [
  MACHINES_DETAILS_ROUTES.registerTraining,
  MACHINES_DETAILS_ROUTES.communicationChat,
  MACHINES_DETAILS_ROUTES.machineCommunicationChat,
];

export type AddComponent = React.FC;

const App: AddComponent = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const isTaskPage = useStoreState(state => state.app.appHeader.isTaskPage);
  const unreadNotificationCount = useStoreState(state => state.notification.unreadNotificationCount);
  const unreadChatCount = useStoreState(state => state.chat.unreadNotificationCount);
  const { pathname } = useLocation();
  const [contentKey, setContentKey] = useState(0);
  const [pageTimestamps, setPageTimestamps] = useState<Record<string, string>>({});

  const handleRefresh = useCallback(() => {
    const pageKey = location.pathname;
    setContentKey(prev => prev + 1);
    setPageTimestamps(prev => ({
      ...prev,
      [pageKey]: new Date().toLocaleTimeString(),
    }));
  }, [location.pathname]);

  const currentTimestamp = pageTimestamps[location.pathname] || '';

  const stripHtmlTags = useCallback((html) => {
    if (!html) return '';
    if (typeof document === 'undefined') return html.replace(/<\/?[^>]+(>|$)/g, '');
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }, []);

  useEffect(() => {
    updateBadgeCount(getTotalUnreadBadgeCount(unreadNotificationCount, unreadChatCount));
  }, [unreadNotificationCount, unreadChatCount]);

  useDidMountEffect(() => {
    const cordova = window['cordova'];
    if (!cordova) {
      console.log("Cordova not available");
      return;
    }

    const navigateByType = (type, machineId, issueId) => {
      console.log("Navigating by type:", { type, machineId, issueId });
      const routesMap = {
        "0": () => {
          console.log("Navigate to Issue Details");
          history.push(
            route(MACHINES_DETAILS_ROUTES.issueDetails, {
              id: machineId,
              issueId,
            })
          );
        },
        "1": () => {
          console.log("Navigate to Service Details");
          history.push(
            route(MACHINES_DETAILS_ROUTES.serviceDetails, {
              id: machineId,
              serviceId: issueId,
            })
          );
        },
        "4": () => {
          console.log("Navigate to Project Chat");
          history.push(
            route(PROJECTS_ROUTES.detail, { projectId: issueId })
          );
        },
        "5": () => {
          console.log("Navigate to Machine Details");
          history.push(
            route(MACHINES_DETAILS_ROUTES.machineCommunicationChat, { id: machineId })
          );
        },
        "102": () => {
          console.log("Navigate to Time Management Details");
          history.push(TIME_MANAGEMENT_ROUTES.details);
        },
        "101": () => {
          console.log("Navigate to Training Details");
          history.push(
            route(TRAINING_ROUTES.details, { id: issueId })
          );
        },
        "100": () => {
          console.log("Navigate to Notifications list");
          history.push(REQUESTS_ROUTES.notifications);
        },
      };

      routesMap[type]?.();
    };

    if (window['FirebasePlugin']) {
      window['FirebasePlugin'].onMessageReceived(
        (message) => {
          console.log("Firebase message received:", message);

          if (message?.messageType !== "notification") {
            console.log("Non-notification message ignored");
            return;
          }

          if (message.badge) {
            const badgeCount = parseInt(message.badge, 10) || 0;
            console.log('badge count', badgeCount);
            updateBadgeCount(badgeCount);
          }

          // Notification tapped
          if (message.tap) {
            console.log("Notification tapped");
            navigateByType(message.type, message.machineId, message.issueId);
          }
        },
        (error) => {
          console.error("Firebase onMessageReceived error:", error);
        }
      );
    }

    const handleResume = () => {
      if (store.getState().app.offline.status === 0) {
        store.getActions().notification.fetchUnreadNotificationsCount();
        store.getActions().chat.fetchUnreadNotificationsCount();
      }
    };

    document.addEventListener('resume', handleResume);

    return () => {
      document.removeEventListener('resume', handleResume);
    };
  });

  useEffect(() => {
    const touchStart = { y: 0, time: 0 };
    const isFlick = { current: false };

    const handleWindowTouchStart = (e: TouchEvent) => {
      touchStart.y = e.touches[0].clientY;
      touchStart.time = Date.now();
      isFlick.current = false;
    };

    const handleWindowTouchMove = (e: TouchEvent) => {
      const diffY = e.touches[0].clientY - touchStart.y;
      const time = Date.now() - touchStart.time;
      if (time > 0 && diffY > 10) {
        const velocity = diffY / time; // pixels per ms
        if (velocity > 1.5) { // 1.5px/ms is a fast flick
          isFlick.current = true;
        }
      }
    };

    window.addEventListener('touchstart', handleWindowTouchStart, { passive: true });
    window.addEventListener('touchmove', handleWindowTouchMove, { passive: true });

    // Initialize PullToRefresh
    PullToRefresh.init({
      mainElement: '#main-scrollable-wrapper',
      triggerElement: '#main-scrollable-wrapper',
      onRefresh() {
        handleRefresh();
      },
      distThreshold: 140,
      distMax: 180,
      resistance: 4.0,
      shouldPullToRefresh() {
        if (isFlick.current || isModalOpen()) return false;
        const isDisabledRoute = DISABLED_ROUTES.some(route => matchPath(pathname, { path: route, exact: false }));
        if (isDisabledRoute) return false;

        const el = document.querySelector('#main-scrollable-wrapper');
        return el ? el.scrollTop === 0 : false;
      },
      instructionsPullToRefresh: t('pullToRefresh.pull'),
      instructionsReleaseToRefresh: t('pullToRefresh.release'),
      instructionsRefreshing: t('pullToRefresh.refreshing'),
    });

    return () => {
      window.removeEventListener('touchstart', handleWindowTouchStart);
      window.removeEventListener('touchmove', handleWindowTouchMove);
      PullToRefresh.destroyAll();
    };
  }, [handleRefresh, stripHtmlTags, history, isTaskPage, contentKey, pathname, t]);

  return (
    <div id={classes['app']} style={{ display: 'flex', flexDirection: 'column' }}>
      <AppHeader/>
      <MainContent key={contentKey}>
        <Messages />

        <Content>
          <AccessControl requiredPermissions={PERMISSION_GROUPS.app} showMessage={true}>
            <Router />
          </AccessControl>
        </Content>
          <Footer  lastUpdated={currentTimestamp}/>
      </MainContent>

      <Sidebar />

      <ToastContainer
        position={toast.POSITION.BOTTOM_CENTER}
        transition={Slide}
        closeButton={false}
        closeOnClick
        hideProgressBar
      />

      <AppLoader />
    </div>
  );
};

export default App;
